import * as React from 'react'
import { Focusable } from './Focusable'
import { Chevron } from './Chevron'
import { classNames } from './helpers'
import type { LinkComponent } from '../types'

export interface PagerLabels {
  /** The previous-page control, e.g. "Previous". */
  previous: string;
  /** The next-page control, e.g. "Next". */
  next: string;
  /**
   * Where you are, already composed and translated by the caller — "Page 2 of 7", or
   * just "Page 2" when the total is unknown. The styleguide ships no copy, and this
   * sentence needs the caller's plural rules either way.
   */
  status: string;
  /** Accessible name for the `<nav>`, e.g. "Pagination". */
  navigation?: string;
}

export interface PagerProps {
  /** The page being shown, 1-based. */
  page: number;
  /**
   * How many pages there are, when that is known. Pass `null` when the source can't
   * be trusted to say (a filtered catalogue listing) — the pager then relies on
   * {@link PagerProps.hasNext} alone and prints no total.
   */
  totalPages?: number | null;
  /**
   * Whether a page follows this one. Only consulted when `totalPages` is unknown;
   * with a total, the last page is simply the last page.
   */
  hasNext?: boolean;
  /** The in-app address of a given page — the caller owns the query-string shape. */
  pageHref: (page: number) => string;
  /**
   * In-app link component (e.g. React Router's `Link`). Without it the pager renders
   * its status only: paging is navigation, and the styleguide holds no router.
   */
  LinkComponent?: LinkComponent;
  labels: PagerLabels;
  className?: string;
}

/**
 * Previous / next paging under a grid.
 *
 * Links rather than buttons, for the same reason the category pills are: a page of
 * the catalogue is a place, so it should be linkable, bookmarkable and reachable
 * with the back button. An unavailable direction is rendered as inert text instead
 * of a disabled link — there is nowhere for it to point, and a link to nowhere is
 * worse than no link.
 *
 * Two pages of one are enough to page a TV grid; there is deliberately no numbered
 * page list, which on a remote is a long row of near-identical stops to cross.
 */
export function Pager ({ page, totalPages = null, hasNext = false, pageHref, LinkComponent, labels, className }: PagerProps) {
  const hasPrevious = page > 1
  const nextAvailable = totalPages !== null ? page < totalPages : hasNext

  // Nothing to page through: one page, and nothing after it.
  if (!hasPrevious && !nextAvailable) return null

  const step = (target: number, direction: 'previous' | 'next', available: boolean) => {
    const label = labels[direction]
    const arrow = <Chevron orientation={direction === 'previous' ? 'left' : 'right'} />
    const content = direction === 'previous'
      ? <>{arrow}<span>{label}</span></>
      : <><span>{label}</span>{arrow}</>

    if (!available || !LinkComponent) {
      return (
        <span className='mlsg-pager__step mlsg-pager__step--inert' aria-disabled='true'>{content}</span>
      )
    }
    return (
      // The colour lives on the wrapper, not the link: the app shell sets
      // `a { color: inherit !important }` globally, so a colour declared on the link
      // itself is discarded and the step would take the page's own text colour.
      <Focusable className='mlsg-focusable--inline mlsg-pager__stop'>
        <LinkComponent to={pageHref(target)} className='mlsg-pager__step' rel={direction}>
          {content}
        </LinkComponent>
      </Focusable>
    )
  }

  return (
    <nav className={classNames('mlsg-pager', className)} aria-label={labels.navigation}>
      {step(page - 1, 'previous', hasPrevious)}
      {/* Announced on change, so a viewer paging with the remote hears where they
          landed without having to hunt for the status line. */}
      <p className='mlsg-pager__status' aria-live='polite'>{labels.status}</p>
      {step(page + 1, 'next', nextAvailable)}
    </nav>
  )
}
